import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  getApiErrorCode,
  getApiErrorDetailId,
  getValidationSizeProblem,
} from "../../../../../api/apiError"
import {
  frameworkSliceKey,
  isRowSupersededInValuesSave,
  isSupersededValuesSave,
  useSaveConditionValues,
  useSetConditionSubstance,
  type DeclarationFrameworkSlice,
} from "../../../../../api/declarationsApi"
import type {
  ConditionFieldRow,
  DeclarationConditionFields,
} from "../../../../../api/types"
import {
  mergeConditionFieldRows,
  normalizeCas,
  rowIdentity,
} from "../mapping/conditionRowMerge"
import { readBindings, withBinding } from "../mapping/reviewerBindings"
import { useConditionRows, useReviewMode } from "../reviewMode"

/** Why a field's last save did not land, as the field tells it (`EditableField`):
 *  `true` a plain failure (the value is reset), `"busy"` the declaration was held
 *  past the save's wait and its one retry (REVIEW_BUSY: nothing written, the typed
 *  value is KEPT as a draft and can be saved again with one click). A values save
 *  is not refused on an approved declaration (main's behaviour, kept 2026-11-03):
 *  it waits for an approval in flight and then lands. */
export type FieldSaveError = true | "busy"

/** The substance a reviewer picked for a nameless slot: what the row shows
 *  while the pick saves, before the refetched rows name the slot. */
export interface PickedSubstance {
  id: string
  name: string
  casNumber: string | null
}

/** One slot's pick in flight, landed, or refused (TUR-1725). `saved` keeps the
 *  picked name on the row only until the pick's own refetch of the card's
 *  read (`/statements`, or the open regulation's slice when paged) has landed,
 *  so the label never flashes back to "Choose substance" in between and never
 *  outlives it: the key is `entry.key`, `NO_CAS` for the card's one nameless
 *  slot, and a nameless slot that is still there after the refetch (articles
 *  skipped as already set, the statement read that does not name a picked
 *  slot) must show "Choose substance", not the old pick. Ended by the refetch
 *  LANDING, never by the card's `conditions` changing: a refetch that answers
 *  the same statements keeps react-query's old reference (structural
 *  sharing), so an identity test never fired on the full read.
 *  `nothing`: the server applied the pick to no article (all of them already
 *  carried a substance, the rest had no slot); the row is the refetched one
 *  and says why, until the next pick.
 *  `error` carries the backend's code (ALREADY_APPROVED, REVIEW_BUSY,
 *  NO_EMPTY_SLOT, ...) and, for the per-article refusals, the article the
 *  server named; the row shows "Choose substance" again: nothing was
 *  written. */
export type SubstancePick =
  | { status: "pending"; substance: PickedSubstance }
  | {
      status: "saved"
      substance: PickedSubstance
      applied: number
      /** Articles of the slot list the server skipped. */
      skipped: SkippedArticles
    }
  /** After the refetch of a pick that skipped some articles: the row is the
   *  refetched one again, and keeps the note until the next pick. */
  | { status: "partial"; applied: number; skipped: SkippedArticles }
  /** Nothing applied: `alreadySet` articles already carried a substance,
   *  `skipped` the rest. Kept until the next pick. */
  | { status: "nothing"; alreadySet: number; skipped: SkippedArticles }
  | {
      status: "error"
      code: string | null
      articleNumber: string | null
      /** A 422 `too_long`: the route's article cap (its `ctx.max_length`). */
      maxArticles: number | null
    }

/** The articles of a slot list a pick skipped, by kind, each with the first
 *  few numbers the server named: `noSlot` have no unnamed entry in the record
 *  yet (the statement's scope reaches them, the last recalculation did not:
 *  they get one when it finishes), `notFound` left the declaration. */
export interface SkippedArticles {
  noSlot: number
  notFound: number
  noSlotArticles: readonly string[]
  notFoundArticles: readonly string[]
}

export function skippedCount(skipped: SkippedArticles): number {
  return skipped.noSlot + skipped.notFound
}

/** The refusal code after which the typed value stays on screen (TUR-1725). */
function keptDraftError(error: unknown): "busy" | null {
  return getApiErrorCode(error) === "REVIEW_BUSY" ? "busy" : null
}

/** Resolves once none of `keys` is fetching: the refetch a gesture's
 *  invalidation started (synchronously, before the gesture's own promise
 *  settled) has landed, failed or been dropped. Read off the query's fetch
 *  state, never its data: a refetch that answers what the cache already holds
 *  keeps react-query's old reference (structural sharing), and an identity
 *  test never sees it land. A read cancelled by a newer invalidation stays
 *  fetching until the newer one lands. */
export function readsSettled(
  queryClient: QueryClient,
  keys: readonly (readonly unknown[])[],
): Promise<void> {
  const cache = queryClient.getQueryCache()
  const fetching = () =>
    keys.some(
      (queryKey) =>
        cache.find({ queryKey, exact: true })?.state.fetchStatus === "fetching",
    )
  if (!fetching()) return Promise.resolve()
  return new Promise((resolve) => {
    const unsubscribe = cache.subscribe(() => {
      if (fetching()) return
      unsubscribe()
      resolve()
    })
  })
}

/**
 * The condition-field WRITE machinery of one statement, shared by every
 * editor surface that saves condition values (`SubstanceEditor`, the
 * substance table, is the only one since wave 5; `SubstanceEditorSheet` owns
 * the instance so its step-3 sign-off can ride the same chain): the
 * declaration's persisted rows plus the three commit gestures, each riding
 * ONE serialised save chain so a field save, an article binding and a
 * substance pick can never race each other's wholesale PUT.
 *
 * Extracted verbatim from the per-article grid editor that wave 5 removed
 * (the doc comments below are the originals and still name its render sites, which
 * remain the reference implementation of each gesture): `latestRowsRef` is
 * the accumulator, `saveChainRef` the serialiser, `localEditsRef` /
 * `localBindingsRef` the two overlays that survive a third-party cache
 * invalidation. `revertVersions` / `fieldErrors` / `substancePicks` are the
 * per-identity UI bookkeeping a caller keys its fields on.
 *
 * `card` needs the statement's address (`id`, `framework`); a landed
 * substance pick's `saved` state ends when the card's read refetch lands
 * (`readsSettled`).
 */
export function useConditionFieldWrites(
  declId: string,
  card: { id: string; framework: string },
) {
  // Flag off: `/condition-fields`. Paged (C3-b2): the open regulation's slice rows.
  const { paged, activeFramework } = useReviewMode()
  const { data } = useConditionRows(declId)
  // THE SIGN-OFFS STAY ON THE SERVER (TUR-0000): this surface writes values
  // only, never a `__verified__` row itself (that is `useVerifyStatement`'s
  // job) — the values-only route strips any anyway, but this surface never
  // builds one in the first place.
  const { mutateAsync: save } = useSaveConditionValues()
  const substanceMutation = useSetConditionSubstance()
  const rows = data?.rows ?? []
  const queryClient = useQueryClient()
  // Same key `useDeclarationConditionFields`/`useSaveConditionFields.onSuccess`
  // read and write (declarationsApi.ts); no shared helper exists for this one
  // query, so it is inlined here exactly as it is there.
  const conditionFieldsQueryKey = [
    "mc",
    "declarations",
    declId,
    "condition-fields",
  ] as const
  // F2 (data loss, review wave 5): the LIVE cache, read at settlement time,
  // never the `data` destructured above. `data` is this ONE render's local
  // binding; `commitMany`/`commitBinding` close over it when the reviewer
  // fires the commit, and their rejection handlers can still run renders
  // later, after a sibling mutation's refetch (a verdict edit, a scope
  // apply, anything routed through `invalidateDeclaration`) has already
  // moved the cache past that snapshot. Rebuilding `latestRowsRef` from the
  // stale closure there silently drops whatever that sibling refetch added,
  // and the NEXT wholesale-replace PUT then deletes it server-side.
  // Paged: the live cache is the card's regulation slice, the one the save paths
  // write into (C3-b1); the full table is not loaded at all.
  const latestPersistedRows = () =>
    paged
      ? (queryClient.getQueryData<DeclarationFrameworkSlice>(
          frameworkSliceKey(declId, card.framework),
        )?.conditionRows ?? [])
      : (queryClient.getQueryData<DeclarationConditionFields>(
          conditionFieldsQueryKey,
        )?.rows ?? [])
  // TUR-1211: a reviewer's own article binding, keyed by CAS (see
  // `conditionsForArticle`'s doc comment for the overlay rule). Recomputed
  // only when the persisted rows change.
  const reviewerBindings = useMemo(
    () => readBindings(rows, card.framework, card.id),
    // `data?.rows` (react-query's own stable reference), never the `rows`
    // const above: that is a fresh `?? []` array on every render even when
    // nothing changed, which would defeat this memo entirely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.rows, card.framework, card.id],
  )
  // TURNUS MEMORY (#1). Gated entirely by the BACKEND: `_memory_read_enabled` fails
  // closed, so an org without the flag gets an empty list and this renders nothing. No
  // second gate here: one authority for whether a suggestion exists at all.
  const memoryHints = data?.memory ?? []

  // REF-BACKED, SERIALISED SAVES: the same "wholesale-replace hazard"
  // `useConformFramework`'s own doc names: `useSaveConditionFields` PUTs the
  // declaration's ENTIRE condition-fields table, so building a save from the
  // render-time `rows` snapshot drops any edit whose network round trip
  // hasn't landed yet. Editing field A, then blurring field B before A's PUT
  // resolves, built B's payload from the pre-A row set: A never reached the
  // server, even though B's request was sent second.
  //
  // `latestRowsRef` is the accumulator: seeded from the query once it loads,
  // then advanced SYNCHRONOUSLY on every commit (before any await), so the
  // very next commit, even one fired before this one's network round trip
  // finishes, always merges onto what THIS edit just wrote. `saveChainRef`
  // then forces the PUTs themselves to run one at a time: without it, two
  // payloads could each already carry every known edit and STILL lose one if
  // their responses land out of order, since a wholesale replace has no
  // server-side merge to fall back on once that happens.
  //
  // THIRD-PARTY INVALIDATION HAZARD. `condition-fields` is a SHARED query key:
  // a sibling mutation on the same declaration (verdict edit, scope apply,
  // confirmStatement, anything routed through `invalidateDeclaration` in
  // declarationsApi.ts) invalidates it too, and that refetch can LAND while a
  // PUT from this editor is still in flight. Wholesale-reassigning
  // `latestRowsRef.current = data.rows` on every such landing would reset the
  // accumulator to a row set the in-flight edit never reached, and the next
  // commit then builds its wholesale-replace payload without that edit and
  // permanently deletes it server-side. `localEditsRef` closes this: it is a
  // by-identity record of every row THIS editor has itself committed, kept
  // (not cleared) once written. The sync effect re-derives the accumulator as
  // `mergeConditionFieldRows(data.rows, [...localEditsRef.current.values()])`:
  // fresh cache state as the base (so rows other editors/mutations wrote,
  // including the bulk gesture's reserved `__verified__`/`__connected__`
  // rows, still flow through), this editor's own edits re-applied on top (so
  // none of THEM can be knocked out by a third party's cache reset). A
  // re-merge of an already-landed own edit is a no-op (same identity, same
  // value), so this is safe to run on every cache update, not just stale
  // ones.
  //
  // SINGLE-CLIENT ONLY. This only serialises writes from THIS editor
  // instance: two browser tabs, or two reviewers, editing the same
  // declaration at once can still race each other at the server. Closing
  // that needs a server-side address-keyed upsert in place of today's
  // wholesale PUT; that is a separate backend ticket, not attempted here.
  const latestRowsRef = useRef<ConditionFieldRow[]>(rows)
  // `unknown`, not `void`: it only ever gates ordering (nothing reads a
  // resolved value off it), but `useSaveConditionFields`'s mutation now
  // resolves the persisted rows rather than `void`, so the chain's own type
  // must accept whatever settles onto it.
  const saveChainRef = useRef<Promise<unknown>>(Promise.resolve())
  const localEditsRef = useRef<Map<string, ConditionFieldRow>>(new Map())
  // SIBLING TO `localEditsRef`, FOR REVIEWER BINDINGS (B2 fix, review, wave
  // 4). A binding does not upsert a row, it REPLACES the whole set of
  // `__connected__` rows for one (framework, statementId, cas) address; it
  // can REMOVE rows, so it cannot ride `localEditsRef`'s by-identity merge,
  // which only ever adds/replaces. Without its own accumulator, a queued
  // binding commit was silently dropped the moment ANY sibling invalidation
  // (a verdict edit, a scope apply, anything routed through
  // `invalidateDeclaration`) landed on `data` before the binding's own save
  // resolved: the resync effect rebuilt `latestRowsRef.current` from fresh
  // `data.rows` plus `localEditsRef` only, the binding was in neither, and
  // the NEXT wholesale-replace PUT then erased it server-side with no error.
  // Keyed by `framework|statementId|normalisedCas`, the binding's own
  // address; kept (not cleared) once written, same lifetime rule as
  // `localEditsRef`.
  const localBindingsRef = useRef<
    Map<
      string,
      {
        framework: string
        statementId: string
        cas: string
        articleNumbers: string[]
      }
    >
  >(new Map())
  const bindingKey = (framework: string, statementId: string, cas: string) =>
    `${framework}|${statementId}|${normalizeCas(cas)}`
  const applyLocalBindings = (
    baseRows: ConditionFieldRow[],
  ): ConditionFieldRow[] => {
    let result = baseRows
    for (const entry of localBindingsRef.current.values()) {
      result = withBinding(
        result,
        entry.framework,
        entry.statementId,
        entry.cas,
        entry.articleNumbers,
      )
    }
    return result
  }

  // TRUE once `rows` (the fresh cache the resync effect below reads) already
  // reflects a `localBindingsRef` entry, so the entry itself can be dropped
  // without losing anything (item 4, review wave 4). Reads `rows` through
  // `readBindings`, the exact function the article-scoping filter
  // (`conditionsForArticle`) itself trusts, so this can never disagree with
  // what the rest of the editor already treats as "bound": a non-empty
  // `articleNumbers` needs an EXACT match (no more, no fewer articles); an
  // empty one (an unbind) needs the row to be gone entirely.
  const bindingSatisfied = (
    rows: readonly ConditionFieldRow[],
    entry: {
      framework: string
      statementId: string
      cas: string
      articleNumbers: string[]
    },
  ): boolean => {
    const current =
      readBindings(rows, entry.framework, entry.statementId).get(
        normalizeCas(entry.cas) as string,
      ) ?? []
    if (entry.articleNumbers.length === 0) return current.length === 0
    if (current.length !== entry.articleNumbers.length) return false
    const want = new Set(entry.articleNumbers)
    return current.every((a) => want.has(a))
  }

  // A FIELD THAT FAILED TO SAVE MUST STOP LOOKING SAVED. `EditableField`
  // below seeds its `local` state from `value` ONCE, on mount, and never
  // resyncs on its own: right for the success path (the next `value` it
  // would see is the very text it just typed, echoed back through the
  // cache), wrong for a rejected save: `value` never moves (the query cache
  // is only ever written on SUCCESS, `useSaveConditionFields`'s own
  // `onSuccess`), so nothing would tell the field to give up the
  // optimistic text it is still showing. Bumping this row's counter changes
  // its `key`, which forces React to remount it: the only way to make an
  // uncontrolled-from-outside `local` re-read a `value` prop that itself
  // never changed. Keyed by identity, not global, so ONE failed field
  // reverts without disturbing whatever the reviewer is still mid-typing in
  // another one.
  const [revertVersions, setRevertVersions] = useState<Record<string, number>>(
    {},
  )

  // B7: a save that actually failed must SAY so on the field, not only revert
  // its text: the revert alone (`revertVersions` above) is silent enough to
  // read as "nothing happened" rather than "this was rejected". Sibling
  // state to `revertVersions`, by the same identity key, for the same
  // reason: `EditableField` remounts on a revert (`revertKey` changes its
  // `key`), which discards ANY state the field tried to hold locally: an
  // error flag has to live up here, in the parent that survives the
  // remount, and be handed back down as a prop to the fresh instance.
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, FieldSaveError>
  >({})

  // A BUSY FIELD'S DRAFT LIVES HERE TOO (TUR-1725). After a REVIEW_BUSY save the
  // edit is out of `localEditsRef` (server truth again) and the typed value is kept
  // only in that field's own `local` state; the paged table unmounts a row scrolled
  // out of its window, and the fresh instance would seed from the PERSISTED value
  // while still showing "busy · Save again", so one click would send the old value.
  // Keyed by the same identity as `fieldErrors`, set with the `"busy"` flag and
  // cleared whenever that flag is: the field seeds from it and "Save again" sends it.
  const [busyDrafts, setBusyDrafts] = useState<Record<string, string>>({})

  // Writes whose latest save failed and was not redone since, by the anchors
  // `fieldErrors` (identity) and `substancePicks` (slot) show them on. A ref
  // beside that state: the step-3 sign-off reads it in the same tick a failure
  // lands, before any re-render (`signAfterSaves`). A busy field (TUR-1725) is
  // one too: its kept draft is not what is stored.
  const failedWritesRef = useRef(new Set<string>())
  const slotFailure = (slotKey: string) => `slot:${slotKey}`

  /** The field `identity` no longer stands as busy or failed. */
  const clearFieldError = (identity: string) => {
    failedWritesRef.current.delete(identity)
    setFieldErrors((prev) => {
      if (!prev[identity]) return prev
      const next = { ...prev }
      delete next[identity]
      return next
    })
    setBusyDrafts((prev) => {
      if (!(identity in prev)) return prev
      const next = { ...prev }
      delete next[identity]
      return next
    })
  }
  /** Re-seed the field `identity` from server truth (remount), with no error. */
  const reseedField = (identity: string) => {
    clearFieldError(identity)
    setRevertVersions((prev) => ({
      ...prev,
      [identity]: (prev[identity] ?? 0) + 1,
    }))
  }

  // One substance pick state PER SLOT, keyed by the slot's `entry.key` (the
  // normalised CAS, or `NO_CAS` for the card's one nameless slot: every
  // CAS-less disclosure folds into it, so one card never has two). A `saved`
  // entry is dropped once the pick's refetch of the card's read has landed
  // (`commitSubstance`), so the key can never carry an old pick onto a
  // nameless slot the refetch still shows. The mutation hook itself is one instance for the whole editor
  // (it has to be, the pick rides the save chain), so its own isPending and
  // isError would bleed a pending spinner or a failure banner onto every
  // other substance-less slot of the card (review, wave 4).
  const [substancePicks, setSubstancePicks] = useState<
    Record<string, SubstancePick>
  >({})
  // Per slot, the pick that currently owns its state: a `saved` state is ended
  // by ITS pick's refetch only, never one a newer pick of the same slot has
  // replaced in the meantime.
  const pickOwnerRef = useRef(new Map<string, object>())
  // The reads the card's own slot list comes from, which the pick's
  // invalidation refetches: flag off, `/statements` (the card's `conditions`),
  // `/condition-fields` (its rows) and `/read-model` (its named slots, the
  // pick read back); paged, the open regulation's slice, which carries all three.
  const cardReadKeys = (): (readonly unknown[])[] =>
    paged
      ? [frameworkSliceKey(declId, activeFramework ?? card.framework)]
      : [
          ["mc", "declarations", declId, "statements"],
          conditionFieldsQueryKey,
          ["mc", "declarations", declId, "read-model"],
        ]

  useEffect(() => {
    if (data) {
      // ITEM 4, REVIEW WAVE 4: clear a `localBindingsRef` entry HERE, once
      // the fresh cache demonstrably already carries it, rather than in
      // `commitBinding`'s own success handler. A refetch that STARTED before
      // the binding's PUT committed can still LAND after it; deleting on
      // success left a window where that stale refetch's rows (still
      // binding-less) were the last word this effect saw, and the very next
      // field save then resent that loss server-side. Bridging it here means
      // the entry survives for as long as it takes: cleared only once `data`
      // itself proves it landed, whichever render that turns out to be.
      for (const [key, entry] of localBindingsRef.current) {
        if (bindingSatisfied(data.rows, entry)) {
          localBindingsRef.current.delete(key)
        }
      }
      const merged = mergeConditionFieldRows(data.rows, [
        ...localEditsRef.current.values(),
      ])
      // Both accumulators, in order: edits first (upsert by identity), then
      // every REMAINING queued binding (which can also remove rows) on top,
      // the same order `commitBinding` itself applies them in (B2 fix,
      // review, wave 4).
      latestRowsRef.current = applyLocalBindings(merged)
    }
  }, [data])

  // ONE BATCH, ONE SAVE, ONE VISIBLE FIELD. `edits` is every row this
  // gesture writes: a single-element array from the per-article rendering,
  // or one row PER RESOLVED ARTICLE from the collapsed blanket rendering
  // (same value fanned out, see the collapse gate below). All of them enter
  // the accumulator and ONE wholesale-replace PUT, exactly like a single
  // edit would; but error/revert bookkeeping is keyed on `edits[0]`'s
  // identity ONLY, because that is the address the ONE rendered
  // `EditableField` is keyed on (`revertKey`/`fieldErrors` at the render
  // site): bumping the other articles' identities would touch fields that
  // are not on screen in collapsed mode and don't exist as instances at all.
  const commitMany = (edits: ConditionFieldRow[]) => {
    const identities = edits.map(rowIdentity)
    for (let i = 0; i < edits.length; i += 1) {
      localEditsRef.current.set(identities[i], edits[i])
    }
    const merged = mergeConditionFieldRows(latestRowsRef.current, edits)
    latestRowsRef.current = merged
    // ONLY THE EDITED ROWS GO ON THE WIRE (PATCH): the server merges them into the
    // stored table. A value typed once on a blanket statement is one row per covered
    // article; shipping the whole table as well broke the 5,000-row cap at 10k articles.
    // ONE input object: the save marks which of its rows a newer save took over
    // on it (`isRowSupersededInValuesSave`, TUR-1725).
    const input = {
      declarationId: declId,
      rows: merged,
      changedRows: edits,
      confirm: false,
      // Paged: answer with what changed, written into the slice (C3-b1).
      echo: paged,
    }
    const send = () => save(input)
    const settled = saveChainRef.current.then(send, send)
    saveChainRef.current = settled
    // The rendered field's identity: the one revert/error anchor (above).
    const uiIdentity = identities[0]
    // A busy draft of this field follows what the reviewer typed since, so a
    // remount while this save is in flight shows this value, not the older one.
    setBusyDrafts((prev) =>
      uiIdentity in prev
        ? { ...prev, [uiIdentity]: edits[0].value ?? "" }
        : prev,
    )
    // Drop this batch's rows from the overlay (only those still THIS batch's
    // objects: a later commit of the same identity owns its entry) and rebuild
    // the accumulator from server truth plus what remains.
    const dropOwnEdits = (which: (i: number) => boolean) => {
      for (let i = 0; i < edits.length; i += 1) {
        if (which(i) && localEditsRef.current.get(identities[i]) === edits[i]) {
          localEditsRef.current.delete(identities[i])
        }
      }
      latestRowsRef.current = applyLocalBindings(
        mergeConditionFieldRows(latestPersistedRows(), [
          ...localEditsRef.current.values(),
        ]),
      )
    }
    // ONE listener, both branches: `.then(onFulfilled, onRejected)` rather
    // than a separate `.then()` plus `.catch()`: two independent listeners
    // on the same promise are fine for the CHAIN (attaching either never
    // disturbs the other or `saveChainRef.current` itself), but a bare
    // `.then(onFulfilled)` with no rejection handler produces its OWN new
    // promise that rejects right along with `settled` and nothing ever
    // observes it: an unhandled rejection on every failed save. Passing
    // both handlers to the SAME `.then()` call keeps it one settled promise.
    settled.then(
      () => {
        // A busy save retried only partly (TUR-1725): a newer save from another
        // surface wrote some of these rows, so their older values were NOT
        // resent and must not linger in the overlay either.
        const taken = edits.map((e) => isRowSupersededInValuesSave(input, e))
        if (taken.some(Boolean)) dropOwnEdits((i) => taken[i])
        // The rendered field's own row was one of them: its value is the newer
        // save's, so it re-seeds from server truth (unless a newer edit of this
        // same field is pending, which reports its own fate).
        if (taken[0] && localEditsRef.current.get(uiIdentity) === undefined) {
          reseedField(uiIdentity)
          return
        }
        // SUCCESS: a LATER commit of the SAME identity clears any standing
        // error: the reviewer re-entered the value and it landed.
        clearFieldError(uiIdentity)
      },
      (error: unknown) => {
        // NEVER REACHED THE SERVER. Believing it locally would let the NEXT,
        // unrelated field's wholesale-replace payload resend these values as
        // if they had saved: drop the WHOLE batch from both trackers (a
        // fan-out fails atomically: it was one PUT) so `commitMany`'s own
        // base for a future edit is server truth again
        // (`useSaveConditionFields`'s own `onError`, declarationsApi.ts, is
        // what tells the reviewer this failed; this only stops the field
        // from lying about it).
        //
        // BUT ONLY THIS BATCH'S OWN ROWS. A LATER commit of the same identity
        // (the reviewer re-edited the field before this PUT came back) has
        // already replaced the map entry with ITS row object and is queued
        // behind this save on the chain. Deleting by identity alone would
        // throw that newer, still-pending edit out of the accumulator, so the
        // NEXT wholesale-replace payload would omit it and delete it
        // server-side. Object identity is the test: the map still points at
        // THIS batch's row exactly when nothing newer has landed on it.
        const uiSuperseded = localEditsRef.current.get(uiIdentity) !== edits[0]
        // Bindings ride on top, exactly as the resync effect rebuilds the
        // accumulator: a binding still in flight when a field save fails
        // must not fall out of the next wholesale-replace payload.
        dropOwnEdits(() => true)
        // Superseded on screen too: a newer edit of the rendered field is
        // pending, so reverting/flagging it now would discard what the
        // reviewer typed AFTER this failed attempt; that newer save reports
        // its own fate through this same handler when it settles.
        if (uiSuperseded) return
        // Superseded by a save from ANOTHER surface (its retry skipped,
        // `isSupersededValuesSave`, or this field's own row left out of a partial
        // retry): a newer save writing this address (or the whole table) decides
        // the field's value, so no "busy · Save again", which would resend the
        // older value over it. Nothing was written here: the field re-seeds from
        // server truth, without an error flag.
        if (
          isSupersededValuesSave(error) ||
          isRowSupersededInValuesSave(input, edits[0])
        ) {
          reseedField(uiIdentity)
          return
        }
        // REVIEW_BUSY (TUR-1725): the server wrote nothing, but the reviewer's
        // value is not wrong, the declaration was only busy (an approval in
        // progress). The field KEEPS the typed text (no revert), says why it is
        // not saved and offers "Save again", which commits the kept text through
        // this same path. The accumulator above is server truth either way.
        const kept = keptDraftError(error)
        if (kept) {
          failedWritesRef.current.add(uiIdentity)
          setFieldErrors((prev) => ({ ...prev, [uiIdentity]: kept }))
          setBusyDrafts((prev) => ({
            ...prev,
            [uiIdentity]: edits[0].value ?? "",
          }))
          return
        }
        reseedField(uiIdentity)
        // B7, the visible counterpart to the silent revert above: a red
        // border + one-line text on the field itself, not just a reset value
        // that could otherwise read as "nothing happened".
        failedWritesRef.current.add(uiIdentity)
        setFieldErrors((prev) => ({ ...prev, [uiIdentity]: true }))
      },
    )
    // RETURNED so a caller can wait for the write. Turnus Memory reports an accept only
    // after the value has actually landed, reporting first would raise an entry's
    // confidence on the strength of a save that then failed. `settled` REJECTS on a failed
    // save: the handlers above are listeners, they do not absorb it for anyone else. The
    // failure is already shown on the field, so a caller chaining on it passes a rejection
    // handler (`.then(ok, () => undefined)`) or awaits it inside a try.
    return settled
  }

  // SIBLING TO `commitMany`, FOR REVIEWER BINDINGS. The reviewer's article
  // binding (`ArticleBindControl` below) can REMOVE rows (unbinding, or
  // swapping A1 for A2), never just upsert them, so it cannot go through
  // `commitMany`'s by-identity `mergeConditionFieldRows`; that API only ever
  // adds/replaces rows, never deletes. `commitBinding` instead records the
  // binding's own address + article set in `localBindingsRef` (B2 fix; that
  // accumulator's own doc comment above says why this is a second
  // accumulator, not a reuse of `localEditsRef`), computes `next` with
  // `withBinding` and follows the same three rules `commitMany` documents
  // above: (a) the accumulator advances synchronously so the very next
  // unrelated commit merges onto this one, (b) the PUT is serialised on the
  // SAME `saveChainRef` so it cannot race a field save, and (c) on
  // rejection the accumulator is rolled back and the identity is flagged in
  // `fieldErrors`, a failed binding save must stop looking saved exactly
  // like a failed field save (B7's own principle above), or the next
  // unrelated field commit would resend the phantom binding as if it had
  // landed.
  //
  // SUPERSEDED CHECK BY REFERENCE ON THE `localBindingsRef` ENTRY, same
  // pattern as `commitMany`'s own `localEditsRef` check: a later commit of
  // the SAME binding address (the reviewer reopened the popover and changed
  // the selection before this save came back) has already replaced the map
  // entry with ITS OWN object and owns reverting or succeeding on its own
  // settle; this call must then leave both the accumulator and the map
  // alone.
  const commitBinding = (
    framework: string,
    statementId: string,
    cas: string,
    articleNumbers: string[],
  ) => {
    const key = bindingKey(framework, statementId, cas)
    const entry = { framework, statementId, cas, articleNumbers }
    localBindingsRef.current.set(key, entry)
    const next = withBinding(
      latestRowsRef.current,
      framework,
      statementId,
      cas,
      articleNumbers,
    )
    latestRowsRef.current = next
    const uiIdentity = rowIdentity({
      articleNumber: "__binding__",
      substanceCas: cas,
      framework,
      fieldKey: "__connected__",
      value: "",
    })
    // BY PATCH, AS A COMMAND: the server applies the same `withBinding` rule to its
    // STORED rows under the lock, so the binding never ships the whole
    // declaration's table (which can pass any row cap on a 10k-article supplier),
    // and a field save queued ahead of it on `saveChainRef` that was rejected can
    // never be resent by it (the old whole-table payload could carry one).
    const run = () =>
      save({
        declarationId: declId,
        // Unused on the PATCH path (the server applies the binding to its stored rows);
        // the hook's input type still names the whole-table field for the PUT callers.
        rows: [],
        bindings: [
          {
            framework,
            statementId,
            // Normalised here as well, so browser and server key the binding identically.
            substanceCas: normalizeCas(cas) ?? cas,
            articleNumbers,
          },
        ],
        confirm: false,
        echo: paged,
      })
    const settled = saveChainRef.current.then(run, run)
    saveChainRef.current = settled
    settled.then(
      () => {
        // ITEM 4, REVIEW WAVE 4: no delete here. The entry is cleared once
        // the resync effect sees `data` demonstrably satisfy it (see
        // `bindingSatisfied`'s own doc comment), deleting on success left a
        // window for a stale, in-flight refetch to land afterwards and wipe
        // the binding from `latestRowsRef` with nothing left to reapply it.
        failedWritesRef.current.delete(uiIdentity)
        setFieldErrors((prev) => {
          if (!prev[uiIdentity]) return prev
          const copy = { ...prev }
          delete copy[uiIdentity]
          return copy
        })
      },
      () => {
        // NEVER REACHED THE SERVER. Only this call's own entry, and only
        // while nothing newer has superseded it (see the doc comment
        // above): a newer commit of the same address owns the map entry
        // and the accumulator now.
        const superseded = localBindingsRef.current.get(key) !== entry
        if (superseded) return
        localBindingsRef.current.delete(key)
        // Rebuild from server truth plus every edit and every REMAINING
        // binding, the same recovery `commitMany`'s own failure branch
        // uses: dropping just this one entry, not resetting to some earlier
        // snapshot that could also discard an unrelated commit that landed
        // in between.
        latestRowsRef.current = applyLocalBindings(
          mergeConditionFieldRows(latestPersistedRows(), [
            ...localEditsRef.current.values(),
          ]),
        )
        failedWritesRef.current.add(uiIdentity)
        setFieldErrors((prev) => ({ ...prev, [uiIdentity]: true }))
      },
    )
    return settled
  }

  // SIBLING TO `commitMany`/`commitBinding`, FOR THE SUBSTANCE-PICK GESTURE
  // (W4-1b; item 2/6, review wave 4). `SubstancePickControl` used to call
  // `useSetConditionSubstance` directly, outside `saveChainRef`, so a queued
  // field blur (`commitMany`) could then race the backend's own record
  // rewrite. Riding the SAME chain fixes that: a field save queued before
  // the substance pick still lands before it, one queued after still lands
  // after. Never touches `latestRowsRef`/`localEditsRef`: the server rekeys
  // the slot and rematerialises the rows itself
  // (`useSetConditionSubstance`'s own doc comment), this call carries no
  // row payload of its own.
  //
  // ONE REQUEST FOR THE WHOLE SLOT (TUR-1725): the picker shows ONE slot
  // standing for `articleNumbers` (item 6), and the server applies the pick to
  // all of them in one transaction (`.../conditions/substance/batch`): one
  // lock, one record write, one rematerialise, one invalidation when it
  // lands. It replaced a sequential per-article loop (~13 s per article,
  // ~38 h at 10,582, half-applied when the tab closed). An article whose slot
  // already carries a substance is skipped by the server (what the loop did
  // with its SLOT_HAS_SUBSTANCE 409), so re-picking a partly applied slot
  // finishes it. An article the record has no slot for, or that the
  // declaration no longer has, is skipped and counted too (the slot list spans
  // the statement's resolved scope, the record the run's own), and the row
  // says how many; the server refuses only when no article had a slot, and
  // then writes nothing. A slot with no
  // article sends nothing; an answer that applied nothing (every article
  // already set) shows no picked name, the refetch shows what is there, with
  // a note saying how many already had a substance and how many had no slot.
  const commitSubstance = (
    slotKey: string,
    articleNumbers: readonly string[],
    substance: PickedSubstance,
  ): Promise<unknown> => {
    if (articleNumbers.length === 0) return Promise.resolve()
    const owner = {}
    pickOwnerRef.current.set(slotKey, owner)
    const owns = () => pickOwnerRef.current.get(slotKey) === owner
    setSubstancePicks((prev) => ({
      ...prev,
      [slotKey]: { status: "pending", substance },
    }))
    const run = () =>
      substanceMutation.mutateAsync({
        declarationId: declId,
        statementId: card.id,
        articleNumbers,
        substanceId: substance.id,
      })
    const settled = saveChainRef.current.then(run, run)
    settled.then(
      ({
        applied,
        alreadySet,
        noSlot,
        notFound,
        noSlotArticles,
        notFoundArticles,
      }) => {
        if (!owns()) return
        failedWritesRef.current.delete(slotFailure(slotKey))
        const skipped: SkippedArticles = {
          noSlot: noSlot ?? 0,
          notFound: notFound ?? 0,
          noSlotArticles: noSlotArticles ?? [],
          notFoundArticles: notFoundArticles ?? [],
        }
        if (applied === 0) {
          // Nothing written: no picked name, the row is the refetched one. Why
          // nothing was saved stays on it (a silent revert read as a lost pick).
          setSubstancePicks((prev) => {
            const copy = { ...prev }
            if ((alreadySet ?? 0) > 0 || skippedCount(skipped) > 0) {
              copy[slotKey] = {
                status: "nothing",
                alreadySet: alreadySet ?? 0,
                skipped,
              }
            } else {
              delete copy[slotKey]
            }
            return copy
          })
          return
        }
        setSubstancePicks((prev) => ({
          ...prev,
          [slotKey]: { status: "saved", substance, applied, skipped },
        }))
        // The picked name stays until the refetch the pick's invalidation
        // started has LANDED (whatever it answers, same reference or not).
        void readsSettled(queryClient, cardReadKeys()).then(() => {
          if (!owns()) return
          setSubstancePicks((prev) => {
            const pick = prev[slotKey]
            if (pick?.status !== "saved") return prev
            const copy = { ...prev }
            if (skippedCount(pick.skipped) > 0) {
              copy[slotKey] = {
                status: "partial",
                applied: pick.applied,
                skipped: pick.skipped,
              }
            } else {
              delete copy[slotKey]
            }
            return copy
          })
        })
      },
      (error: unknown) => {
        if (!owns()) return
        failedWritesRef.current.add(slotFailure(slotKey))
        setSubstancePicks((prev) => ({
          ...prev,
          [slotKey]: {
            status: "error",
            code: getApiErrorCode(error),
            articleNumber: getApiErrorDetailId(error, "articleNumber"),
            maxArticles: getValidationSizeProblem(error)?.max ?? null,
          },
        }))
      },
    )
    // A pick's rows reach the cache by REFETCH (the mutation only
    // invalidates), unlike a field save's, which its own response writes. The
    // chain link settles once that refetch has landed, so a sign-off queued
    // behind it (`signAfterSaves`) finds its rows in the cache, as it does a
    // field save's. The refetch the invalidation started is joined, never
    // restarted.
    const refetched = () =>
      queryClient
        .refetchQueries(
          { queryKey: conditionFieldsQueryKey, exact: true },
          { cancelRefetch: false },
        )
        .then(
          () => undefined,
          () => undefined,
        )
    saveChainRef.current = settled.then(refetched, refetched)
    return settled
  }

  // THE STEP-3 SIGN-OFF RIDES THE SAVE CHAIN (`SubstanceEditorSheet`). `sign`
  // runs as one more link: after every write queued before it has settled with
  // its rows in the cache (see `commitSubstance` for a pick), and ahead of every
  // write queued after it, so no save can overlap the sign-off. It does not run
  // while a write has failed and was not redone: signing would attest the
  // stored value, not the one the reviewer entered. The answer is "unsaved".
  const signAfterSaves = <T>(
    sign: () => Promise<T>,
  ): Promise<T | "unsaved"> => {
    const run = (): Promise<T | "unsaved"> =>
      failedWritesRef.current.size > 0 ? Promise.resolve("unsaved") : sign()
    const settled = saveChainRef.current.then(run, run)
    saveChainRef.current = settled.then(
      () => undefined,
      () => undefined,
    )
    return settled
  }

  return {
    /** The condition-fields query result (react-query's own stable
     *  reference: memo on `data?.rows`, never on `rows`). */
    data,
    /** `data?.rows ?? []`, a fresh array every render. */
    rows,
    reviewerBindings,
    memoryHints,
    commitMany,
    commitBinding,
    commitSubstance,
    signAfterSaves,
    revertVersions,
    fieldErrors,
    /** A `"busy"` field's kept draft by identity (TUR-1725): seeds a remounted
     *  field and is what its "Save again" sends. */
    busyDrafts,
    substancePicks,
  }
}

/** One statement's condition-field writes, as `useConditionFieldWrites` returns them. */
export type ConditionFieldWrites = ReturnType<typeof useConditionFieldWrites>
